/**
 * Trading React Query Hooks
 * Custom hooks for trading operations using React Query for server state
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { appApi } from '@/domains/trading/api';
import { queryKeys, staleTimeConfig, mutationHelpers } from '@/shared/providers/react-query/client';
import type { OrderPlacement } from '@/domains/trading/validation/trading';

// =============================================================================
// ACCOUNT HOOKS
// =============================================================================

/**
 * Hook to fetch trading account information
 */
export function useTradingAccount() {
  return useQuery({
    queryKey: queryKeys.trading.all,
    queryFn: async () => {
      const response = await appApi.trading.getAccount();
      return response.data;
    },
    staleTime: staleTimeConfig.portfolio,
    retry: 2,
  });
}

// =============================================================================
// ORDERS HOOKS
// =============================================================================

/**
 * Hook to fetch user's orders
 */
export function useOrders(filters?: { status?: string; symbol?: string; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.trading.orders.list(filters),
    queryFn: async () => {
      const response = await appApi.trading.getOrders();
      return response.data;
    },
    staleTime: staleTimeConfig.orders,
    refetchInterval: 30000, // Refetch every 30 seconds for active orders
  });
}

/**
 * Hook to fetch a specific order by ID
 */
export function useOrder(orderId: string) {
  return useQuery({
    queryKey: queryKeys.trading.orders.detail(orderId),
    queryFn: async () => {
      const response = await appApi.trading.getOrder(orderId);
      return response.data;
    },
    staleTime: staleTimeConfig.orders,
    enabled: !!orderId,
  });
}

/**
 * Hook to fetch order history
 */
export function useOrderHistory(filters?: { symbol?: string; startDate?: string; endDate?: string }) {
  return useQuery({
    queryKey: queryKeys.trading.orders.history(undefined, filters),
    queryFn: async () => {
      const response = await appApi.trading.getOrderHistory(filters);
      return response.data;
    },
    staleTime: staleTimeConfig.performance,
  });
}

// =============================================================================
// ORDER MUTATIONS
// =============================================================================

/**
 * Hook to place a new order
 */
export function usePlaceOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData: OrderPlacement) => {
      // Map frontend OrderPlacement → QTP models.OrderRequest JSON fields
      const apiOrderData = {
        symbol: orderData.symbol,
        side: orderData.side,
        order_type: orderData.order_type,
        quantity: orderData.quantity,
        time_in_force: orderData.time_in_force,
        client_order_id: orderData.client_order_id,
        ...(('price' in orderData) && orderData.price != null
          ? { limit_price: orderData.price, price: orderData.price }
          : {}),
        ...(('stop_price' in orderData) && orderData.stop_price != null
          ? { stop_price: orderData.stop_price }
          : {}),
      };

      const response = await appApi.trading.placeOrder(apiOrderData);
      return response.data;
    },

    onSuccess: (newOrder) => {
      // Invalidate and refetch orders
      queryClient.invalidateQueries({ queryKey: queryKeys.trading.orders.all() });

      // Optionally update account data if needed
      queryClient.invalidateQueries({ queryKey: queryKeys.trading.all });

      // If we have positions, invalidate them too
      queryClient.invalidateQueries({ queryKey: queryKeys.portfolio.all });
    },

    onError: (error: unknown) => {
      const err = error as { status?: number; message?: string };
      if (err?.status === 401) {
        // Soft-fail: clear messaging, no forced redirect from the mutation layer
        console.warn(
          err.message || 'Authentication required. Sign in again to continue trading.'
        );
        return;
      }
      console.error('Failed to place order:', error);
    },
  });
}

/**
 * Hook to cancel an order
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const response = await appApi.trading.cancelOrder(orderId);
      return response.data;
    },

    onSuccess: (_, orderId) => {
      // Optimistically update the order in cache
      queryClient.setQueryData(
        queryKeys.trading.orders.detail(orderId),
        (oldOrder: any) => oldOrder ? { ...oldOrder, status: 'cancelled' } : oldOrder
      );

      // Invalidate orders list
      queryClient.invalidateQueries({ queryKey: queryKeys.trading.orders.all() });
    },
  });
}

/**
 * Hook to modify an existing order
 */
export function useModifyOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, updates }: { orderId: string; updates: Partial<OrderPlacement> }) => {
      // Map frontend OrderPlacement updates → QTP models.OrderRequest JSON fields
      const apiUpdates: any = {};
      if (updates.quantity !== undefined) apiUpdates.quantity = updates.quantity;
      if (updates.order_type !== undefined) apiUpdates.order_type = updates.order_type;
      if (updates.time_in_force !== undefined) apiUpdates.time_in_force = updates.time_in_force;
      if (updates.client_order_id !== undefined) apiUpdates.client_order_id = updates.client_order_id;
      if ('price' in updates && updates.price !== undefined) {
        apiUpdates.limit_price = updates.price;
        apiUpdates.price = updates.price;
      }
      if ('stop_price' in updates && updates.stop_price !== undefined) {
        apiUpdates.stop_price = updates.stop_price;
      }

      const response = await appApi.trading.modifyOrder(orderId, apiUpdates);
      return response.data;
    },

    onSuccess: (updatedOrder, { orderId }) => {
      // Update the specific order in cache
      queryClient.setQueryData(
        queryKeys.trading.orders.detail(orderId),
        updatedOrder
      );

      // Invalidate orders list
      queryClient.invalidateQueries({ queryKey: queryKeys.trading.orders.all() });
    },
  });
}

// =============================================================================
// POSITIONS HOOKS
// =============================================================================

/**
 * Hook to fetch current positions
 */
export function usePositions() {
  return useQuery({
    queryKey: queryKeys.trading.positions.all(),
    queryFn: async () => {
      const response = await appApi.trading.getPositions();
      return response.data;
    },
    staleTime: staleTimeConfig.positions,
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook to fetch a specific position
 */
export function usePosition(symbol: string) {
  return useQuery({
    queryKey: queryKeys.trading.positions.detail(symbol),
    queryFn: async () => {
      const response = await appApi.trading.getPosition(symbol);
      return response.data;
    },
    staleTime: staleTimeConfig.positions,
    enabled: !!symbol,
  });
}

// =============================================================================
// PORTFOLIO HOOKS
// =============================================================================

/**
 * Hook to fetch portfolio overview
 */
export function usePortfolioOverview() {
  return useQuery({
    queryKey: queryKeys.portfolio.overview(),
    queryFn: async () => {
      const response = await appApi.portfolio.getPortfolioOverview();
      return response.data;
    },
    staleTime: staleTimeConfig.portfolio,
    refetchInterval: 60000,
  });
}

/**
 * Hook to fetch portfolio performance
 */
export function usePortfolioPerformance(timeframe: string = '1D') {
  return useQuery({
    queryKey: queryKeys.portfolio.performance(undefined, timeframe),
    queryFn: async () => {
      const response = await appApi.portfolio.getPerformance(timeframe);
      return response.data;
    },
    staleTime: staleTimeConfig.performance,
  });
}

// =============================================================================
// REAL-TIME HOOKS
// =============================================================================

/**
 * Hook for real-time order updates
 */
export function useOrderUpdates() {
  const queryClient = useQueryClient();

  const handleOrderUpdate = useCallback((orderUpdate: any) => {
    // Update specific order in cache
    queryClient.setQueryData(
      queryKeys.trading.orders.detail(orderUpdate.id),
      orderUpdate
    );

    // Invalidate orders list to trigger refetch
    queryClient.invalidateQueries({
      queryKey: queryKeys.trading.orders.all(),
      exact: false
    });
  }, [queryClient]);

  const handlePositionUpdate = useCallback((positionUpdate: any) => {
    // Update specific position in cache
    queryClient.setQueryData(
      queryKeys.trading.positions.detail(positionUpdate.symbol),
      positionUpdate
    );

    // Invalidate positions list
    queryClient.invalidateQueries({
      queryKey: queryKeys.trading.positions.all(),
      exact: false
    });

    // Also invalidate portfolio data
    queryClient.invalidateQueries({
      queryKey: queryKeys.portfolio.all,
      exact: false
    });
  }, [queryClient]);

  return {
    handleOrderUpdate,
    handlePositionUpdate,
  };
}

// =============================================================================
// BATCH OPERATIONS
// =============================================================================

/**
 * Hook to cancel multiple orders
 */
export function useBatchCancelOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderIds: string[]) => {
      const promises = orderIds.map(id => appApi.trading.cancelOrder(id));
      const results = await Promise.allSettled(promises);
      return results;
    },

    onSuccess: () => {
      // Invalidate all orders data
      queryClient.invalidateQueries({ queryKey: queryKeys.trading.orders.all() });
    },
  });
}

// =============================================================================
// PREFETCHING UTILITIES
// =============================================================================

/**
 * Hook to prefetch trading data
 */
export function useTradingPrefetch() {
  const queryClient = useQueryClient();

  const prefetchOrders = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.trading.orders.list(),
      queryFn: async () => {
        const response = await appApi.trading.getOrders();
        return response.data;
      },
      staleTime: staleTimeConfig.orders,
    });
  }, [queryClient]);

  const prefetchPositions = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.trading.positions.all(),
      queryFn: async () => {
        const response = await appApi.trading.getPositions();
        return response.data;
      },
      staleTime: staleTimeConfig.positions,
    });
  }, [queryClient]);

  const prefetchAccount = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.trading.all,
      queryFn: async () => {
        const response = await appApi.trading.getAccount();
        return response.data;
      },
      staleTime: staleTimeConfig.portfolio,
    });
  }, [queryClient]);

  const prefetchAll = useCallback(() => {
    prefetchAccount();
    prefetchOrders();
    prefetchPositions();
  }, [prefetchAccount, prefetchOrders, prefetchPositions]);

  return {
    prefetchOrders,
    prefetchPositions,
    prefetchAccount,
    prefetchAll,
  };
}

// =============================================================================
// COMBINED HOOKS
// =============================================================================

/**
 * Hook that combines multiple trading queries for dashboard
 */
export function useTradingDashboard() {
  const account = useTradingAccount();
  const orders = useOrders({ limit: 10 }); // Recent orders
  const positions = usePositions();
  const portfolio = usePortfolioOverview();

  return {
    account,
    orders,
    positions,
    portfolio,
    isLoading: account.isLoading || orders.isLoading || positions.isLoading || portfolio.isLoading,
    error: account.error || orders.error || positions.error || portfolio.error,
    refetchAll: () => {
      account.refetch();
      orders.refetch();
      positions.refetch();
      portfolio.refetch();
    },
  };
}
